<template>
    <flex-element
        v-on-clickaway="collapse"
        :element-type="elementType"
        :class="['icon-form', stateClass]"
        class-default-inline="f-inline-flex f-border-b-1 f-px-0.25 f-justify-end"
        :style="containerStyle"
        role="search"
    >
        <a-input
            v-show="!isCollapsed"
            ref="input"
            class="f-absolute f-left-0"
            class-default="f-border-0 f-mr-1 f-px-0 f-py-0 f-text-0.8125 f-text-gray-800 f-font-paragraph f-min-h-1/1"
            aria-label="Search the website. Type your keywords, then hit enter to submit."
            v-bind="element(1)"
            @keyup="onKeyup"
            @element-updated="elementUpdated"
        />

        <button type="button" @click="expand">
            <a-icon
                ref="icon"
                class-default="f-text-0.875 f-text-gray-600 f-text-gray-800 f-p-0.25"
                icon="search"
                set="fas"
                v-bind="element(2)"
                @element-updated="elementUpdated"
            />
        </button>
    </flex-element>
</template>

<script>
import { mapState } from 'vuex';
import { mixin as clickaway } from 'vue-clickaway';

import baseOrganism from '@/mixins/base-organism/base-organism-smart';
import { STATE_CLASSES } from '@/data/constants/classes';

const COLLAPSE_ATTR_NAME = 'icon-search-collapse';
const URL_ATTR_NAME = 'icon-search-url';
const DEFAULT_INPUT_WIDTH = 182;

export default {
    name: 'icon-search-form',
    mixins: [baseOrganism, clickaway],

    props: {
        displayName: {
            type: String,
            default: 'Icon Search Form',
        },
    },

    data() {
        return {
            cssDisplay: 'inline-flex',
            iconWidth: 0,
            inputWidth: DEFAULT_INPUT_WIDTH,
            isCollapsed: true,
            isCollapsible: true,
            keywords: '',
        };
    },

    computed: {
        ...mapState({
            previewMode: state => state.previewMode,
            changedOption: state => (state.page ? state.page.changedOption : false),
            selectedElements: state => (state.page ? state.page.selectedElements : []),
        }),

        containerStyle() {
            const width = this.isCollapsed
                ? this.iconWidth
                : this.iconWidth + this.inputWidth;

            return {
                width: `${ width }px`,
            };
        },

        searchUrl() {
            return this.getElementData(URL_ATTR_NAME);
        },

        stateClass() {
            return {
                [STATE_CLASSES.COLLAPSED]: this.isCollapsed,
            };
        },
    },

    watch: {
        // recalculate the inline style width if either the input or icon element is selected, and a width-related option was changed
        // ... this is needed for the animation to run properly
        changedOption({ option, value }) {
            const widthRelatedOptions = ['width', 'min-width', 'max-width', 'padding', 'font-size'];

            const inputElement = this.$refs.input.$el;
            const iconElement = this.$refs.icon.$el;
            const selectedElement = this.selectedElements[0].$el;

            if (widthRelatedOptions.includes(option)) {
                if (selectedElement === inputElement || selectedElement === iconElement) {
                    this.storeInputWidth();
                    this.storeIconWidth();
                }
            }

            if (option === 'icon-search-collapse') {
                this.isCollapsible = value;
                this.isCollapsed = value;
            }
        },

        isCollapsed() {
            if (!this.isCollapsed) {
                this.$refs.input.$el.querySelector('input').focus();
            }
        },
    },

    created() {
        if (!this.searchUrl) {
            this.setElementData(URL_ATTR_NAME, '/search-results', { triggerChangeDetection: false });
        }
    },

    async mounted() {
        await this.$nextTick();
        await this.$nextTick();

        this.storeInputWidth();
        this.storeIconWidth();

        this.isCollapsible = !this.hasElementData(COLLAPSE_ATTR_NAME) || !!this.getElementData(COLLAPSE_ATTR_NAME);

        if (!this.isCollapsible) {
            this.isCollapsed = false;
        }
    },

    methods: {
        storeInputWidth() {
            // detect if the user manually set the input width, if so, use that for the animation
            const widthClassPrefixes = ['f-w', 'f-min-w', 'f-max-w'];
            const existingWidthClass = this.$refs.input
                && this.$refs.input.htmlClass.find((className) => {
                    return widthClassPrefixes.some(prefix => className.startsWith(prefix));
                });

            if (existingWidthClass) {
                const widthRem = Number(existingWidthClass.slice(existingWidthClass.lastIndexOf('-') + 1));

                this.inputWidth = widthRem * 16;
            }
        },

        storeIconWidth() {
            let iconWidth = this.$refs.icon ? this.$refs.icon.$el.offsetWidth : 0;

            // if zero, it most likely means that we were unable to detect the width
            // likely because the search icon is nested inside a container that is hidden
            // in this case we'll fall back to a (usually) reasonable value of 24px
            if (iconWidth === 0) iconWidth = 24;

            this.iconWidth = iconWidth;
        },

        expand() {
            if (!this.isCollapsible) return;

            this.isCollapsed = false;
        },

        collapse() {
            if (!this.previewMode || !this.isCollapsible) return;

            this.isCollapsed = true;
        },

        submit() {
            window.location.href = `${ this.searchUrl }?query=${ this.keywords }`;
        },

        onKeyup(e) {
            if (this.previewMode && e.key === 'Enter' && this.keywords.length) {
                this.submit();
            } else {
                this.keywords = e.target.value;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.icon-form {
    position: relative;
    overflow: hidden;
    min-height: 1.875rem;
    transition: width 0.5s;

    &.#{ $is-collapsed } {
        border: none !important;
    }
}
</style>
